/**
 * IPD prescription (CPOE order book) status logic.
 *
 * Import-free on purpose — `pharmacyMappers` pulls in `api/inventory-request`, and
 * with it axios ESM, which Jest cannot load. Keeping these predicates here (same
 * reasoning as dispensingEntity / opdPrescriptionEntity) makes them unit-testable.
 *
 * The backend vocabulary is fixed and forward-only (main/ipd/manage_prescription.py):
 *
 *   Sent -> Acknowledged -> Dispensed -> Received      (Cancel sits outside the chain)
 *
 * Those raw values are what we store, send AND display. Everything user-facing goes
 * through PRESCRIPTION_STATUS_LABELS, which restores the backend's casing without
 * renaming anything — a chip reads "Sent", exactly like the payload behind it.
 */
import { PRESCRIPTION_STATUSES, PRESCRIPTION_STATUS_LABELS } from '../constants';

/** Normalized codes, grouped by where they sit in the pharmacy's workflow. */
const PENDING_STATUSES = ['ACTIVE', 'SENT'];
const IN_PROGRESS_STATUSES = ['ACKNOWLEDGED'];
const COMPLETED_STATUSES = ['DISPENSED', 'RECEIVED'];
const CANCELLED_STATUSES = ['CANCEL', 'CANCELLED'];

/**
 * Only reachable through mock/demo row shapes — `/get_ipd_prescriptions/` cannot
 * produce them. Kept so those rows still count as open work, but never offered as
 * a filter option.
 */
const LEGACY_PARTIAL_STATUSES = ['PARTIALLY_ISSUED', 'PARTIALLY_DISPENSED'];

export function normalizePrescriptionStatus(status) {
  const raw = String(status || '').trim();
  if (!raw) return 'ACTIVE';
  if (/^sent$/i.test(raw)) return 'SENT';
  if (/^partially[\s_-]?issued$/i.test(raw)) return 'PARTIALLY_ISSUED';
  if (/^partially[\s_-]?dispensed$/i.test(raw)) return 'PARTIALLY_DISPENSED';
  if (/^issued$/i.test(raw)) return 'ISSUED';
  if (/^cancelled$/i.test(raw)) return 'CANCELLED';
  return raw.toUpperCase().replace(/\s+/g, '_');
}

/** Order reached the pharmacy, nothing picked up yet. */
export function isPendingPrescriptionStatus(status) {
  return PENDING_STATUSES.includes(normalizePrescriptionStatus(status));
}

/** A pharmacist has opened the dispense flow; work started, not finished. */
export function isInProgressPrescriptionStatus(status) {
  return IN_PROGRESS_STATUSES.includes(normalizePrescriptionStatus(status));
}

/** Terminal, successfully: pharmacy dispensed it, or the ward confirmed receipt. */
export function isCompletedPrescriptionStatus(status) {
  return COMPLETED_STATUSES.includes(normalizePrescriptionStatus(status));
}

/** Terminal, withdrawn: the doctor stopped the order. */
export function isCancelledPrescriptionStatus(status) {
  return CANCELLED_STATUSES.includes(normalizePrescriptionStatus(status));
}

/**
 * Open work — pending OR in progress.
 *
 * ACKNOWLEDGED is deliberately included. It used to be excluded, which dropped an
 * order off the default list the moment a pharmacist opened it (the Start Dispense
 * page acknowledges on load) and made the sidebar badge drift away from the card.
 */
export function isActivePrescriptionStatus(status) {
  const normalized = normalizePrescriptionStatus(status);
  return PENDING_STATUSES.includes(normalized)
    || IN_PROGRESS_STATUSES.includes(normalized)
    || LEGACY_PARTIAL_STATUSES.includes(normalized);
}

export function prescriptionStatusLabel(status) {
  const normalized = normalizePrescriptionStatus(status);
  return PRESCRIPTION_STATUS_LABELS[normalized] || normalized.replace(/_/g, ' ');
}

/**
 * Status dropdown. `ACTIVE` is a frontend *group*, not a backend status — it is the
 * wire value `filterPrescriptions` already understands, relabelled so it reads as
 * the group it is. Everything below it is a real backend status.
 */
export const PRESCRIPTION_STATUS_FILTER_OPTIONS = [
  { value: 'ACTIVE', label: 'Open (Sent + Acknowledged)' },
  { value: '', label: 'All statuses' },
  ...PRESCRIPTION_STATUSES.map((value) => ({
    value,
    label: PRESCRIPTION_STATUS_LABELS[value] || value.replace(/_/g, ' '),
  })),
];

export const DEFAULT_PRESCRIPTION_STATUS_FILTER = 'ACTIVE';

/** Does a row's status satisfy the selected filter? Empty filter matches everything. */
export function matchesPrescriptionStatusFilter(status, filterValue) {
  if (!filterValue) return true;
  if (filterValue === 'ACTIVE') return isActivePrescriptionStatus(status);
  if (filterValue === 'COMPLETED') return isCompletedPrescriptionStatus(status);
  const normalized = normalizePrescriptionStatus(status);
  // A blank status is treated as Sent everywhere else, so the Pending filter must
  // catch it too — otherwise those rows are only reachable via "All statuses".
  if (filterValue === 'SENT') return isPendingPrescriptionStatus(status);
  return normalized === filterValue;
}

export const PRESCRIPTION_ROW_ACTION = {
  START: 'START',
  CONTINUE: 'CONTINUE',
  VIEW_ONLY: 'VIEW_ONLY',
  NONE: 'NONE',
};

/** Statuses the pharmacy can still act on. Terminal orders are not dispensable. */
export function isDispensablePrescriptionStatus(status) {
  return isActivePrescriptionStatus(status);
}

/**
 * Which primary action a table row offers.
 *
 *   Sent / blank   -> Start Dispense
 *   Acknowledged   -> Continue Dispense   (same destination, honest label)
 *   Dispensed / Received / Cancel -> nothing; the row is view-only via the eye icon
 *
 * Without the dispense permission an actionable row shows the existing "View only"
 * note; a terminal row shows nothing, because there is no action being withheld.
 * This only stops offering transitions the backend would reject — it adds no new
 * operation.
 */
export function resolvePrescriptionRowAction(status, canStart) {
  if (!isDispensablePrescriptionStatus(status)) {
    return { mode: PRESCRIPTION_ROW_ACTION.NONE, label: '' };
  }
  if (!canStart) {
    return { mode: PRESCRIPTION_ROW_ACTION.VIEW_ONLY, label: 'View only' };
  }
  if (isInProgressPrescriptionStatus(status)) {
    return { mode: PRESCRIPTION_ROW_ACTION.CONTINUE, label: 'Continue Dispense' };
  }
  return { mode: PRESCRIPTION_ROW_ACTION.START, label: 'Start Dispense' };
}

/**
 * Keys that would carry controlled-drug information if the API ever sent it.
 * `/get_ipd_prescriptions/` currently serializes medicines as
 * `{id, medicine_name, generic_medicine_names, details}` with
 * `details = {dose, frequency, duration}`, so none of these exist today.
 */
const ROW_CONTROLLED_KEYS = ['has_controlled', 'controlled_count'];
const LINE_CONTROLLED_KEYS = [
  'controlled',
  'is_controlled_drug',
  'is_controlled',
  'is_narcotic',
  'schedule',
  'drug_schedule',
];

function hasAnyKey(source, keys) {
  if (!source || typeof source !== 'object') return false;
  return keys.some((key) => source[key] !== undefined);
}

/**
 * Whether the payload actually *carries* controlled-drug data — key presence, not
 * truthiness. A response that reports `controlled: false` on every line is real
 * data (answer: none), and must be shown as 0; a response with no such key at all
 * is missing data, and must not be reported as 0.
 *
 * `medicines` is passed in rather than re-derived so this file stays import-free.
 */
export function hasControlledDataSignal(row, medicines = []) {
  if (hasAnyKey(row, ROW_CONTROLLED_KEYS)) return true;
  return (Array.isArray(medicines) ? medicines : []).some(
    (line) => hasAnyKey(line, LINE_CONTROLLED_KEYS) || hasAnyKey(line?.details, LINE_CONTROLLED_KEYS),
  );
}
